"use client";

import { useEffect, useState } from "react";

interface CompletenessState {
	loading: boolean;
	hasResume: boolean;
	missingRequired: string[];
}

const INITIAL: CompletenessState = {
	loading: true,
	hasResume: true,
	missingRequired: [],
};

/**
 * Client hook that fetches `/api/profile/completeness` on mount. Returns
 * a benign default (`hasResume: true`, no missing keys) while loading
 * and on any error, so the caller never blocks the Apply CTA. The
 * JobApplyCta uses this to render a one-line "profile incomplete"
 * warning above the Apply button — UX is purely additive.
 *
 * `enabled` should be `false` for unauthenticated users to skip the
 * 401-triggering fetch.
 *
 * Also refetches on `pageshow` with `event.persisted === true` — the
 * "Upload resume" / "Complete your profile" links this banner points to
 * (`/resume`, `/settings`) live in the separately-deployed `apps/app` zone
 * (apps/CLAUDE.md), so returning here is a real browser navigation, not a
 * Next.js soft nav. Pressing Back after finishing the upload can restore
 * this component from the browser's back/forward cache instead of
 * remounting it, which would otherwise keep showing the pre-upload
 * snapshot fetched before the user left.
 */
export function useProfileCompleteness(enabled: boolean): CompletenessState {
	const [state, setState] = useState<CompletenessState>(INITIAL);

	useEffect(() => {
		if (!enabled) {
			setState({ loading: false, hasResume: true, missingRequired: [] });
			return;
		}
		let cancelled = false;
		const load = async () => {
			try {
				const resp = await fetch("/api/profile/completeness", {
					method: "GET",
					credentials: "include",
					cache: "no-store",
					headers: { Accept: "application/json" },
				});
				if (!resp.ok) {
					if (!cancelled) {
						setState({ loading: false, hasResume: true, missingRequired: [] });
					}
					return;
				}
				const data = (await resp.json()) as {
					hasResume?: boolean;
					missingRequired?: string[];
				};
				if (cancelled) return;
				setState({
					loading: false,
					hasResume: Boolean(data.hasResume),
					missingRequired: Array.isArray(data.missingRequired)
						? data.missingRequired
						: [],
				});
			} catch {
				if (!cancelled) {
					setState({ loading: false, hasResume: true, missingRequired: [] });
				}
			}
		};
		void load();

		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) void load();
		};
		window.addEventListener("pageshow", onPageShow);
		return () => {
			cancelled = true;
			window.removeEventListener("pageshow", onPageShow);
		};
	}, [enabled]);

	return state;
}
